<template>
  <div class="author-info">
    <div class="required-info">
      <img
        class="avatar"
        :src="avatarLink"
        :alt="author.fullName"
        loading="lazy"
      />
      <div class="info">
        <div class="info__name">{{ author.fullName }}</div>
        <div
          v-if="showReaderBadge"
          class="info__reader-experience"
        >
          Автор-читатель
        </div>
        <div
          v-if="!author.isRedaction"
          class="info__static-info"
        >
          Автор статей на Трипстере
        </div>
      </div>
    </div>

    <div
      v-if="author.description"
      class="description"
    >
      {{ author.description }}
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import type { Author } from '@/pages/author/entities/author';
  import { useJournalContent } from '@/entities/journalContent';

  const store = useJournalContent();
  const { articles } = storeToRefs(store);

  // Показываем бейдж только после загрузки статей
  const showReaderBadge = computed(() => articles.value.some((article) => article.isUgc));

  interface Props {
    author: Author;
  }

  const props = defineProps<Props>();

  const defaultAvatarUrl = new URL('@/app/assets/default-avatar.png', import.meta.url).href;
  const avatarLink = computed(() => props.author.avatarLink || defaultAvatarUrl);
</script>

<style lang="scss" scoped>
  .required-info {
    display: flex;
    gap: var(--alias-space-24);

    @include tablet {
      flex-direction: column;
      gap: var(--alias-space-16);
    }
  }

  .avatar {
    flex-grow: 0;
    flex-shrink: 0;
    object-fit: cover;
    width: 120px;
    height: 120px;
    border-radius: var(--alias-radius-rounded);
    background-color: var(--alias-color-background-default);
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-4);
    justify-content: center;

    &__name {
      @include base-1000-950-extra;

      @include tablet {
        @include base-800-750-extra;
      }
    }

    &__static-info {
      color: var(--alias-color-text-subtle);

      @include base-400-400-regular;
    }
  }

  .description {
    max-width: 600px;
    margin-top: var(--alias-space-24);

    @include base-400-400-regular;

    @include tablet {
      max-width: none;
      margin-top: var(--alias-space-8);
    }
  }

  .info__reader-experience {
    width: fit-content;
    padding: 4px 8px;
    border-radius: 8px;
    background-color: var(--alias-color-background-accent-orange);
    color: var(--alias-color-text-inverse);

    @include base-300-300-medium;
  }
</style>
